const CONSUMER_MARKETING_ATTRIBUTION_PARAMS = ['papa_source', 'papa_placement']

export function parseConsumerMarketingAttribution(search = '') {
  const searchParams = new URLSearchParams(search)

  return CONSUMER_MARKETING_ATTRIBUTION_PARAMS.reduce((attribution, paramName) => {
    const value = searchParams.get(paramName)?.trim()

    if (value) attribution[paramName] = value

    return attribution
  }, {})
}

export function createConsumerMarketingAnalyticsContext({
  search = window.location.search,
  pageVariant = window.PAGE_VARIANT,
} = {}) {
  // Capture once so delayed consent or later URL changes cannot alter landing attribution.
  const attribution = Object.freeze(parseConsumerMarketingAttribution(search))

  return Object.freeze({
    attribution,
    eventProperties: (properties = {}) => ({
      ...properties,
      pageVariant,
      ...attribution,
    }),
  })
}

function defaultSegmentEventProperties(properties = {}) {
  return { ...properties, pageVariant: window.PAGE_VARIANT }
}

function trackGoogleAnalyticsEvent(action, params) {
  if (window.GOOGLE_ANALYTICS_ID) {
    window.papaGtagEvent?.(action, {
      ...params,
      send_to: window.GOOGLE_ANALYTICS_ID,
    })
  }
}

export function initSegment(eventProperties = defaultSegmentEventProperties) {
  // Create a queue, but don't obliterate an existing one!
  const analytics = (window.analytics = window.analytics || [])

  if (analytics.invoked) console?.error?.('Segment snippet included twice.')
  // If the real analytics.js is already on the page return.
  if (analytics.initialize || analytics.invoked) return

  // Invoked flag, to make sure the snippet is never invoked twice.
  analytics.invoked = true

  // A list of the methods in Analytics.js to stub.
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'page',
    'once',
    'off',
    'on',
    'deregister',
    'register',
    'setAnonymousId',
  ]
  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so we can replay the data.
  analytics.factory = function (method) {
    return function () {
      const args = Array.prototype.slice.call(arguments)
      args.unshift(method)
      analytics.push(args)
      return analytics
    }
  }
  // For each of our methods, generate a queueing stub.
  for (let i = 0; i < analytics.methods.length; i++) {
    const key = analytics.methods[i]
    analytics[key] = analytics.factory(key)
  }
  // Define a method to load Analytics.js from our CDN,
  // and that will be sure to only ever load it once.
  analytics.load = function (key, options) {
    // Create an async script element based on your key.
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js'
    // Insert our script next to the first script element.
    const first = document.getElementsByTagName('script')[0]
    first.parentNode.insertBefore(script, first)
    analytics._loadOptions = options
  }

  // Add a version to keep track of what's in the wild.
  analytics.SNIPPET_VERSION = '5.2.0'

  // Load Analytics.js with your key, which will automatically
  // load the tools you've enabled for your account. Boosh!
  analytics.load(window.CONSUMER_MARKETING_SEGMENT_WRITE_KEY)

  // Record the initial landing pageview. Segment's analytics.js does not fire
  // this on its own -- the canonical snippet calls page() right after load().
  // This is queued now and replayed once the library loads, and only runs at
  // all because initSegment() is gated behind analytics consent.
  analytics.page(eventProperties())

  const faqsContainer = window.FAQS_ID && document.getElementById(window.FAQS_ID)

  if (faqsContainer) {
    // Each FAQ renders as a native <details data-faq-item data-faq-question="…">
    // (see the `faq_item` component). The `toggle` event doesn't bubble, so we
    // listen in the capture phase to catch it from any FAQ in the container, and
    // only track when one is opened.
    faqsContainer.addEventListener(
      'toggle',
      function (e) {
        const item = e.target
        if (!item.matches || !item.matches('[data-faq-item]') || !item.open) return

        const title = (item.dataset.faqQuestion || '').trim()
        if (title) {
          window.analytics.track('FAQ - ' + title, eventProperties())
          trackGoogleAnalyticsEvent('consumer_faq_opened', { faq_question: title })
        }
      },
      true,
    )
  }

  document.addEventListener('click', function (e) {
    const el = e.target.closest('[data-event-name]')

    if (el) {
      const eventName = el.dataset.eventName

      window.analytics.track(eventName, eventProperties())
      trackGoogleAnalyticsEvent('consumer_cta_clicked', { cta_name: eventName })
    }
  })
}

export function initFacebookPixel() {
  // If the Facebook Pixel is already on the page, return.
  if (window.fbq) return
  // Create the `fbq` function. Until the real fbevents.js loads, calls are
  // pushed onto a queue so you never have to wait for the script to load to
  // start recording events.
  const fbq = (window.fbq = function () {
    fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments)
  })
  // Keep a global reference and set the metadata the loader expects.
  if (!window._fbq) window._fbq = fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  // Create an async script element for fbevents.js and insert it next to
  // the first script element on the page.
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const first = document.getElementsByTagName('script')[0]
  first.parentNode.insertBefore(script, first)
  // Initialize the pixel and record the initial page view.
  fbq('init', window.FB_PIXEL_ID)
  fbq('track', 'PageView')
}

// Loads the Google tag (gtag.js) library once and configures GA4 / Google Ads
// per granted consent. Idempotent -- safe to call on every consent change: the
// library is injected once, and each product is configured at most once.
//   analytics consent -> GA4         (window.GOOGLE_ANALYTICS_ID)
//   marketing consent -> Google Ads  (window.GOOGLE_ADS_ID)
// gtag() and window.dataLayer are defined inline in the layout <head>.
let gtagLibraryLoaded = false
const gtagConfigured = {}

export function initGoogleTag({ analytics, marketing, attribution = {} }) {
  // `analytics`/`marketing` are the consent.js OSANO_CATEGORY booleans.
  const ids = []
  if (analytics && window.GOOGLE_ANALYTICS_ID) ids.push(window.GOOGLE_ANALYTICS_ID)
  if (marketing && window.GOOGLE_ADS_ID) ids.push(window.GOOGLE_ADS_ID)
  if (ids.length === 0) return

  // gtag is a global fn declared in the layout <head>; bail if it's missing.
  if (typeof window.gtag !== 'function') return

  // Inject gtag.js once. Any configured id works in the loader URL -- it pulls
  // the same library regardless.
  if (!gtagLibraryLoaded) {
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(ids[0])
    const first = document.getElementsByTagName('script')[0]
    first.parentNode.insertBefore(script, first)
    window.gtag('js', new Date())
    gtagLibraryLoaded = true
  }

  // Configure each product once, only while its category is consented.
  ids.forEach(id => {
    if (!gtagConfigured[id]) {
      // Config-scoped custom parameters apply to the automatic page_view and later GA4 events.
      if (id === window.GOOGLE_ANALYTICS_ID && Object.keys(attribution).length > 0) {
        window.gtag('config', id, { ...attribution })
      } else {
        window.gtag('config', id)
      }

      gtagConfigured[id] = true
    }
  })
}
